import { useNavigate } from '@tanstack/react-router'
import type { ActivityTrend, BackofficeNumbersResponse } from 'api/generated'
import { changePct } from 'helpers/daily-report'
import { Loader } from 'lucide-react'
import { useAppSelector } from 'store/store'
import { MetricTile } from './MetricTile'

const number = new Intl.NumberFormat('it-IT')
// Redis counters arrive as numeric strings
const n = (value: number | string) => number.format(Number(value))

type Trends = BackofficeNumbersResponse['data']['activity_trends']

const percent = new Intl.NumberFormat('it-IT', {
  style: 'percent',
  maximumFractionDigits: 1,
})
const matchRate = (matched: number, created: number) =>
  created ? percent.format(matched / created) : '—'

const ACTIVITIES: { key: keyof Trends; label: string; title?: string }[] = [
  { key: 'smartwatch_workouts', label: 'Workout smartwatch' },
  {
    key: 'trainings',
    label: 'Allenamenti registrati',
    title: 'Esclusi quelli generati dalle ricorrenze',
  },
  { key: 'friendly_matches', label: 'Partite amichevoli' },
  {
    key: 'training_schedules',
    label: 'Allenamenti programmati',
    title:
      'Allenamenti ricorrenti con una regola attiva oggi; 24h e 7 giorni = allenamenti resi ricorrenti nel periodo',
  },
]

/** Counters computed now by the backend (home/backoffice_numbers, every 60 s). */
export const LiveNumbers = () => {
  const navigate = useNavigate()
  const home = useAppSelector((state) => state.homeRequests)

  // The initial state is all zeros: wait for the first answer instead of showing them
  if (!home.users && !home.error)
    return <Loader className='h-5 w-5 animate-spin' />

  const tiles = [
    {
      label: 'Utenti registrati',
      value: n(home.users),
      note: `+${n(home.usersCreatedLastMonth)} nell'ultimo mese`,
      onSelect: () => navigate({ to: '/utenti' }),
    },
    { label: 'Attivi 24h', value: n(home.activeUsers), note: 'ultime 24 ore' },
    { label: 'Attivi 30 giorni', value: n(home.activeUsers1m) },
    { label: 'Attivi 365 giorni', value: n(home.activeUsers1y) },
    { label: 'Nuovi utenti 24h', value: n(home.newUsersLast24h) },
    { label: 'Nuovi utenti 7 giorni', value: n(home.newUsersLast1w) },
    {
      label: 'Codici membership 24h',
      value: n(home.membershipCodes24h),
      onSelect: () => navigate({ to: '/codici-membership' }),
    },
    {
      label: 'Codici membership 7 giorni',
      value: n(home.membershipCodes1w),
      change: changePct(home.membershipCodes1w, home.membershipCodes1wAgo),
      changeTitle: 'Rispetto ai 7 giorni prima',
      onSelect: () => navigate({ to: '/codici-membership' }),
    },
    {
      label: 'Codici membership 30 giorni',
      value: n(home.membershipCodes1m),
      change: changePct(home.membershipCodes1m, home.membershipCodes1mAgo),
      changeTitle: 'Rispetto ai 30 giorni prima',
      onSelect: () => navigate({ to: '/codici-membership' }),
    },
    {
      label: 'Messaggi 24h',
      value: n(home.messagesLast24h),
      onSelect: () => navigate({ to: '/messaggi' }),
    },
    {
      label: 'Messaggi 7 giorni',
      value: n(home.messagesLast1w),
      onSelect: () => navigate({ to: '/messaggi' }),
    },
    { label: 'Visite 24h', value: n(home.visitEventsLast24h) },
    { label: 'Visite 7 giorni', value: n(home.visitEventsLast1w) },
  ]
  const trendTile = (label: string, trend: ActivityTrend, title?: string) => ({
    label,
    title,
    value: n(trend.total),
    note: `+${n(trend.last_24h)} in 24h · +${n(trend.last_1w)} in 7 giorni`,
    change: changePct(trend.last_1w, trend.previous_1w),
    changeTitle: `7 giorni rispetto ai 7 prima (${n(trend.previous_1w)})`,
  })
  const trends = home.activityTrends
  const activityTiles = trends
    ? ACTIVITIES.map(({ key, label, title }) =>
        trendTile(label, trends[key], title)
      )
    : []

  // Match Finder: friendly matches organised in the app, played or not
  const finder = home.matchFinder
  const matchFinderTiles = finder
    ? [
        {
          label: 'Utenti trovabili',
          title: 'Visibili in «Giocano vicino a te» (non bannati)',
          value: n(finder.findable_users),
        },
        trendTile(
          'Partite create',
          finder.matches,
          'Partite organizzate, giocate o no (escluse quelle caricate dopo averle giocate)'
        ),
        {
          ...trendTile(
            'Partite matchate',
            finder.matched,
            'Il creatore ha accettato almeno una richiesta di iscrizione (per data della prima accettazione)'
          ),
          note: `${matchRate(finder.matched.total, finder.matches.total)} delle create · +${n(finder.matched.last_1w)} in 7 giorni`,
        },
        {
          label: 'Aperte ora',
          title: 'Partite aperte non ancora iniziate',
          value: n(finder.open_now),
        },
      ]
    : []

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5'>
        {tiles.map((tile) => (
          <MetricTile key={tile.label} {...tile} />
        ))}
      </div>
      {activityTiles.length > 0 && (
        <div className='space-y-2'>
          <h3 className='text-sm font-semibold'>Funzioni dell&apos;app</h3>
          <div className='grid grid-cols-2 gap-2 lg:grid-cols-4'>
            {activityTiles.map((tile) => (
              <MetricTile key={tile.label} {...tile} />
            ))}
          </div>
        </div>
      )}
      {matchFinderTiles.length > 0 && (
        <div className='space-y-2'>
          <h3 className='text-sm font-semibold'>Trova partita</h3>
          <div className='grid grid-cols-2 gap-2 lg:grid-cols-4'>
            {matchFinderTiles.map((tile) => (
              <MetricTile key={tile.label} {...tile} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
